import { EventEmitter } from '@angular/core';
import { Customer } from './customer';
import { Filter } from './filter';
import { User } from './user.interface';

class StorageInterface {
    public static user: User;
}

export class LocalStorage implements StorageInterface {
    public static customerChange: EventEmitter<any> = new EventEmitter();
    public static user: User;
    static version: { datetime?: string; buildId?: string; buildNumber?: string; environment?: string };

    public static setRestoreData(customerId: number, projectId: number, zoom: number, lat: number, lng: number) {
        localStorage.setItem('restoreData', JSON.stringify({ customerId, projectId, zoom, lat, lng }));
    }

    public static getRestoreData(): { customerId: number; projectId: number; zoom: number; lat: number; lng: number } {
        const restoreData = localStorage.getItem('restoreData')
            ? JSON.parse(localStorage.getItem('restoreData'))
            : null;
        // Migrate restore data saved before townId was renamed to customerId.
        if (restoreData?.townId && !restoreData.customerId) {
            restoreData.customerId = restoreData.townId;
            delete restoreData.townId;
            localStorage.setItem('restoreData', JSON.stringify(restoreData));
        }
        return restoreData;
    }

    public static resetRestoreData() {
        localStorage.removeItem('restoreData');
    }

    public static setUserToken(token) {
        localStorage.setItem('access_token', token);
    }

    public static getRegister() {
        const lsr = localStorage.getItem('register');
        return lsr === 'false' ? false : null;
    }

    public static setRegister(register: boolean) {
        localStorage.setItem('register', register ? 'true' : 'false');
    }

    public static setExpireTimeToken(token) {
        localStorage.setItem('token_date', new Date().toISOString());
        localStorage.setItem('expire_access_token', token);
    }

    public static getTokenDate() {
        return localStorage.getItem('token_date');
    }

    public static removeTokenDate() {
        localStorage.setItem('token_date', '');
        localStorage.setItem('expire_access_token', '');
    }

    public static getExpireTimeToken() {
        return localStorage.getItem('expire_access_token');
    }

    public static setUser(user: User) {
        this.user = user;
        localStorage.setItem('register', 'false');
        localStorage.setItem('user', JSON.stringify(user));
    }

    public static getUser(): User {
        return JSON.parse(localStorage.getItem('user'));
    }

    public static setMapSettings(mapSettings: Object) {
        localStorage.setItem('mapSettings', JSON.stringify(mapSettings));
    }

    public static getMapSettings(): Object {
        return JSON.parse(localStorage.getItem('mapSettings'));
    }

    public static setFilter(filter: Object) {
        localStorage.setItem('filter', JSON.stringify(filter));
    }

    public static getFilter(): Filter {
        return JSON.parse(localStorage.getItem('filter'));
    }

    public static logoutUser() {
        delete this.user;
        localStorage.clear();
        localStorage.setItem('register', 'false');
    }

    public static setParentUser(token, user: User) {
        localStorage.setItem('parent_access_token', token);
        localStorage.setItem('parent_user', JSON.stringify(user));
    }

    public static unsetParentUser() {
        localStorage.removeItem('parent_access_token');
        localStorage.removeItem('parent_user');
    }

    public static getParentUserToken() {
        return localStorage.getItem('parent_access_token');
    }

    public static getParentUser(): User {
        return JSON.parse(localStorage.getItem('parent_user'));
    }

    /**
     * Rounded to ~1 km, sent with every request (X-Latest-Location) to keep the auth session's location current.
     */
    public static setLatestLocation(coords: { latitude: number; longitude: number }) {
        localStorage.setItem('latest_location', `${coords.latitude.toFixed(2)},${coords.longitude.toFixed(2)}`);
    }

    public static getLatestLocation() {
        return localStorage.getItem('latest_location');
    }

    public static getUserToken() {
        return localStorage.getItem('access_token');
    }

    public static setLastActivity(date) {
        localStorage.setItem('lastActivity', date);
    }

    public static getLastActivity() {
        return localStorage.getItem('lastActivity');
    }

    public static saveSettings(settings: object) {
        localStorage.setItem('settings', JSON.stringify(settings));
    }

    public static getSettings() {
        const settings = JSON.parse(localStorage.getItem('settings'));
        return settings ? settings : {};
    }

    public static getQrRedirectCount() {
        const qrRedirectCountKey = 'qr-redirect-count';
        let qrRedirectCount = +localStorage.getItem(qrRedirectCountKey);
        qrRedirectCount = qrRedirectCount ? qrRedirectCount + 1 : 0;
        localStorage.setItem(qrRedirectCountKey, '' + qrRedirectCount);
        return qrRedirectCount;
    }

    public static setCustomer(customer: Customer) {
        if (customer) {
            localStorage.setItem('customer', JSON.stringify(customer));
            LocalStorage.customerChange.emit();
        }
    }

    public static getCustomer(): Customer {
        let customer = localStorage.getItem('customer');
        // Migrate customer data saved under the previous local-storage key.
        if (!customer && localStorage.getItem('town')) {
            customer = localStorage.getItem('town');
            localStorage.setItem('customer', customer);
            localStorage.removeItem('town');
        }
        if (!customer) {
            return null;
        }
        const parsed: Customer = JSON.parse(customer);
        if (typeof parsed.area === 'string') {
            parsed.area = JSON.parse(parsed.area);
        }
        return parsed;
    }

    public static setSearchProjectsIds(ids: number[]) {
        localStorage.setItem('searchProjectsIds', JSON.stringify(ids));
    }

    public static getSearchProjectsIds(): number[] {
        return JSON.parse(localStorage.getItem('searchProjectsIds'));
    }

    public static setCustomersSortAlphabetically(sortAlphabetically: boolean) {
        localStorage.setItem('customersSortAlphabetically', sortAlphabetically ? 'true' : 'false');
    }

    public static getCustomersSortAlphabetically(): boolean {
        return localStorage.getItem('customersSortAlphabetically') === 'true';
    }

    public static getMail(): string {
        return localStorage.getItem('mail') || '';
    }

    public static setMail(mail: string) {
        localStorage.setItem('mail', mail);
    }
}
